import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Container,
  Fade,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  Grid,
  CircularProgress,
  Alert,
} from "@mui/material";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useAuth, useAppointments } from "../../contexts";
import { Appointment } from "../../@data";
import { getAppointmentsByClientAndDateRange } from "../../services/appointments";
import { formatPrice, formatDuration, canCancelAppointment } from "../../utils";
import {
  CancelAppointmentButton,
  CancelAppointmentModal,
} from "../../components";
import { CancelAppointmentModalRef } from "../../components/cancel-appointment-modal";
import { Navigate } from "react-router-dom";

const History: React.FC = () => {
  const { client } = useAuth();
  const { deleteAppointment } = useAppointments();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancellingAppointment, setCancellingAppointment] = useState(false);
  const cancelModalRef = useRef<CancelAppointmentModalRef>(null);

  const formatAppointmentDate = (timestamp: number) => {
    return format(timestamp, "EEEE, dd 'de' MMMM 'às' HH:mm", { locale: ptBR });
  };

  const getStatusColor = (appointment: Appointment) => {
    const now = Date.now();
    const appointmentTime = appointment.timestamp;

    if (appointmentTime < now) {
      return "default"; // Passado
    } else if (appointment.confirmed) {
      return "success"; // Confirmado
    } else {
      return "warning"; // Pendente
    }
  };

  const getStatusText = (appointment: Appointment) => {
    const now = Date.now();
    const appointmentTime = appointment.timestamp;

    if (appointmentTime < now) {
      return "Realizado";
    } else if (appointment.confirmed) {
      return "Confirmado";
    } else {
      return "Pendente";
    }
  };

  const handleCancelAppointment = (appointment: Appointment) => {
    cancelModalRef.current?.open(appointment);
  };

  const handleConfirmCancel = async (appointment: Appointment) => {
    setCancellingAppointment(true);
    try {
      await deleteAppointment(appointment.id);
      // Remove o agendamento da lista local
      setAppointments((prev) =>
        prev.filter((app) => app.id !== appointment.id)
      );
      cancelModalRef.current?.close();
    } catch (error) {
      console.error("Erro ao cancelar agendamento:", error);
    } finally {
      setCancellingAppointment(false);
    }
  };

  useEffect(() => {
    const fetchAppointments = async () => {
      if (!client?.id) {
        setLoading(false);
        setError("Usuário não identificado");
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Busca agendamentos dos últimos 30 dias até 1 ano no futuro
        const now = Date.now();
        const thirtyDaysAgo = now - 30 * 24 * 60 * 60 * 1000;
        const oneYearFromNow = now + 365 * 24 * 60 * 60 * 1000;

        const clientAppointments = await getAppointmentsByClientAndDateRange(
          client.id,
          thirtyDaysAgo,
          oneYearFromNow
        );

        // Ordena por data (mais recentes primeiro)
        const sortedAppointments = clientAppointments.sort(
          (a, b) => b.timestamp - a.timestamp
        );

        setAppointments(sortedAppointments);
      } catch (err) {
        console.error("Erro ao buscar agendamentos:", err);
        setError("Erro ao carregar agendamentos");
      } finally {
        setLoading(false);
      }
    };

    fetchAppointments();
  }, [client?.id]);

  if (!client?.id) {
    return <Navigate to="/" />;
  }

  return (
    <Fade in timeout={1000}>
      <Container maxWidth="md" sx={{ py: 2, mb: 8 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" gutterBottom>
            📋 Meus agendamentos
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Abaixo segue a lista de seus agendamentos.
          </Typography>
        </Box>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && appointments.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Você ainda não possui agendamentos.
          </Alert>
        )}

        {!loading && !error && appointments.length > 0 && (
          <Grid container spacing={2}>
            {appointments.map((appointment) => (
              <Grid item xs={12} key={appointment.id}>
                <Card sx={{ borderRadius: "1rem", position: "relative" }}>
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 2,
                      }}
                    >
                      <Avatar
                        src={appointment.professional.imageUrl}
                        sx={{ width: 48, height: 48 }}
                      >
                        {appointment.professional.name.charAt(0)}
                      </Avatar>

                      <Box sx={{ flex: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Typography variant="body1">
                            {appointment.service.name}
                          </Typography>
                          {getStatusText(appointment) !== "Pendente" && (
                            <Chip
                              label={getStatusText(appointment)}
                              color={getStatusColor(appointment) as any}
                              size="small"
                            />
                          )}
                        </Box>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Profissional: {appointment.professional.name}
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Data e Horário:{" "}
                          {formatAppointmentDate(appointment.timestamp)}
                        </Typography>

                        <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                          <Typography
                            variant="body2"
                            color="primary"
                            fontWeight={500}
                          >
                            {formatPrice(appointment.service.price)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ⏰ {formatDuration(appointment.service.duration)}
                          </Typography>
                        </Box>

                        {appointment.observation && (
                          <Box
                            sx={{
                              mt: 2,
                              p: 2,
                              bgcolor: "grey.50",
                              borderRadius: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontWeight: 500, mb: 0.5 }}
                            >
                              📝 Observação:
                            </Typography>
                            <Typography variant="body2" color="text.primary">
                              {appointment.observation}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                  {canCancelAppointment(appointment) && (
                    <CancelAppointmentButton
                      appointment={appointment}
                      onCancel={handleCancelAppointment}
                    />
                  )}
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
        <CancelAppointmentModal
          ref={cancelModalRef}
          onConfirm={handleConfirmCancel}
          loading={cancellingAppointment}
        />
      </Container>
    </Fade>
  );
};

export default History;
